import { z } from "zod";
import { RichText } from "../RichText";
import { ctaColorEnum, ctaClasses } from "../ctaColors";
import { useRenderContext, type BookingTypeEntry } from "../RenderContext";
import { globalSectionSchema, subHeadingLevel } from "../globalProps";
import { CARD_SHADOW } from "../cardStyles";
import { SectionShell } from "../SectionShell";

export const schema = globalSectionSchema.extend({
  heading: z.string().default("Simple, Transparent Pricing"),
  subheading: z.string().optional(),
  layout: z.enum(["panels", "rate-table"]).default("panels"),
  highlightIndex: z.number().int().min(-1).nullable().default(null),
  includedPlanIds: z.array(z.string()).nullable().default(null),
  ctaLabel: z.string().default("Book Now"),
  ctaHref: z.string().default("/book"),
  ctaColor: ctaColorEnum.optional(),
  showCta: z.boolean().default(true),
  cadence: z.string().default("/minute"),
  billingDisplay: z.enum(["flat", "per-minute", "per-hour"]).default("flat"),
  footnote: z.string().optional(),
  planBlurbs: z.record(z.string(), z.string()).default({}),
});

export type Props = z.infer<typeof schema>;
export const defaults: Props = schema.parse({});

function formatPrice(rate: number): string {
  return `$${rate.toFixed(2)}`;
}

/** Derive display price and cadence label from the billing display mode. */
function deriveBilling(
  rate: number,
  billingDisplay: "flat" | "per-minute" | "per-hour",
  cadence: string,
): { displayPrice: string; cadenceLabel: string; explanation: string | null } {
  switch (billingDisplay) {
    case "per-minute":
      return {
        displayPrice: formatPrice(rate / 60),
        cadenceLabel: "/min",
        explanation: "Billed by the minute",
      };
    case "per-hour":
      return {
        displayPrice: formatPrice(rate),
        cadenceLabel: "/hr",
        explanation: "Billed by the hour",
      };
    default: // flat
      return {
        displayPrice: formatPrice(rate),
        cadenceLabel: cadence,
        explanation: null,
      };
  }
}

/* ---------- Layout: Panels (default) ---------- */

function PanelsLayout({
  bookingTypes,
  ctaLabel,
  ctaHref,
  ctaColor,
  showCta,
  cadence,
  billingDisplay,
  highlightIndex,
  planBlurbs,
  SubH,
}: {
  bookingTypes: BookingTypeEntry[];
  ctaLabel: string;
  ctaHref: string;
  ctaColor: string | undefined;
  showCta: boolean;
  cadence: string;
  billingDisplay: "flat" | "per-minute" | "per-hour";
  highlightIndex: number | null;
  planBlurbs: Record<string, string>;
  SubH: "h2" | "h3" | "h4";
}) {
  // Cards per row at desktop: pack rows as evenly as possible, max 3 across
  // (1/2/3 → one row, 4 → 2×2, 5 → 3+2, 6 → 3×2, 7 → 3+3+1, ...).
  const count = bookingTypes.length;
  const cols = (count <= 3 ? count : Math.ceil(count / Math.ceil(count / 3))) as 1 | 2 | 3;
  const CONTAINER_MAX = { 1: "max-w-sm", 2: "max-w-2xl", 3: "max-w-5xl" } as const;
  const CARD_WIDTH = {
    1: "w-full",
    2: "w-full @md:w-[calc(50%-1rem)]",
    3: "w-full @md:w-[calc(50%-1rem)] @lg:w-[calc(33.333%-1.34rem)]",
  } as const;
  return (
    <div className={`flex flex-wrap justify-center gap-8 mx-auto ${CONTAINER_MAX[cols]}`}>
      {bookingTypes.map((bt, i) => {
        const rate = bt.pricingPlan?.hourlyRate ?? bt.startingPrice;
        const billing = rate != null ? deriveBilling(rate, billingDisplay, cadence) : null;
        let highlighted: boolean;
        if (highlightIndex === null) {
          // Auto: highlight middle plan for 2-3 plans
          highlighted = bookingTypes.length <= 3 && i === Math.floor(bookingTypes.length / 2) && bookingTypes.length > 1;
        } else if (highlightIndex === -1) {
          highlighted = false;
        } else {
          highlighted = i === highlightIndex;
        }

        return (
          <div
            key={bt.id}
            className={`${CARD_WIDTH[cols]} rounded-lg p-8 flex flex-col ${
              highlighted
                ? "bg-primary text-white ring-2 ring-primary shadow-lg scale-105"
                : `bg-white text-text ${CARD_SHADOW}`
            }`}
          >
            <SubH className="text-xl font-semibold mb-2">{bt.name}</SubH>
            <div className="mb-6">
              {billing ? (
                <>
                  <span className="text-4xl font-bold">{billing.displayPrice}</span>
                  <span className="text-lg opacity-80">{billing.cadenceLabel}</span>
                  {billing.explanation && (
                    <p className="text-sm opacity-70 mt-1">{billing.explanation}</p>
                  )}
                </>
              ) : (
                <span className="text-2xl font-semibold opacity-80">Contact us</span>
              )}
            </div>
            {(planBlurbs[bt.id] || bt.description) && (
              <p className="flex-1 mb-8 opacity-80 leading-relaxed">
                {planBlurbs[bt.id] || bt.description}
              </p>
            )}
            {!planBlurbs[bt.id] && !bt.description && (
              <div className="flex-1 mb-8" />
            )}
            {showCta && ctaLabel && ctaHref && (
              <a
                href={ctaHref}
                className={`block text-center font-semibold py-3 rounded-lg transition-opacity ${ctaClasses(ctaColor || "primary", "solid", highlighted && !ctaColor)}`}
              >
                {ctaLabel}
              </a>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ---------- Layout: Rate Table ---------- */

function RateTableLayout({
  bookingTypes,
  cadence,
  billingDisplay,
  planBlurbs,
}: {
  bookingTypes: BookingTypeEntry[];
  cadence: string;
  billingDisplay: "flat" | "per-minute" | "per-hour";
  planBlurbs: Record<string, string>;
}) {
  return (
    <div className="max-w-4xl mx-auto overflow-x-auto">
      <table className="w-full text-left border-collapse">
        <thead>
          <tr className="border-b-2 border-current/15">
            <th className="py-3 pr-4 font-semibold">Service</th>
            <th className="py-3 pr-4 font-semibold hidden @md:table-cell">Description</th>
            <th className="py-3 pr-4 font-semibold text-right">Price</th>
            <th className="py-3 font-semibold text-right hidden @md:table-cell">Unit</th>
          </tr>
        </thead>
        <tbody>
          {bookingTypes.map((bt) => {
            const rate = bt.pricingPlan?.hourlyRate ?? bt.startingPrice;
            const billing = rate != null ? deriveBilling(rate, billingDisplay, cadence) : null;

            return (
              <tr key={bt.id} className="border-b border-current/10">
                <td className="py-4 pr-4 font-medium">
                  {bt.name}
                  {(planBlurbs[bt.id] || bt.description) && (
                    <div className="@md:hidden text-sm opacity-70 mt-1">
                      {planBlurbs[bt.id] || bt.description}
                    </div>
                  )}
                </td>
                <td className="py-4 pr-4 opacity-70 hidden @md:table-cell">
                  {planBlurbs[bt.id] || bt.description || ""}
                </td>
                <td className="py-4 pr-4 text-right font-semibold text-lg">
                  {billing ? billing.displayPrice : "Contact us"}
                </td>
                <td className="py-4 text-right opacity-70 hidden @md:table-cell">
                  {billing ? billing.cadenceLabel.replace(/^\//, "per ") : ""}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ---------- Main component ---------- */

export function Component(props: Props) {
  const result = schema.safeParse(props);
  const safe = result.success ? result.data : defaults;
  const ctx = useRenderContext();
  const allBookingTypes: BookingTypeEntry[] = ctx.bookingTypes || [];
  const bookingTypes = safe.includedPlanIds
    ? allBookingTypes.filter((bt) => safe.includedPlanIds!.includes(bt.id))
    : allBookingTypes;
  const SubH = subHeadingLevel(safe.headingLevel);

  if (bookingTypes.length === 0) {
    if (!ctx.isEditor) return null;
    return (
      <SectionShell globals={safe}>
          <div className="text-center">
            <RichText
              as={safe.headingLevel}
              html={safe.heading}
              className="text-3xl @md:text-[2.5rem] font-bold mb-4"
              data-prop="heading"
            />
            {safe.subheading && (
              <RichText
                as="div"
                html={safe.subheading}
                className="text-lg opacity-80 max-w-2xl mx-auto mb-6"
                data-prop="subheading"
              />
            )}
            <p className="text-text-muted">No services published yet.</p>
          </div>
      </SectionShell>
    );
  }

  return (
    <SectionShell globals={safe}>
        <div className="text-center mb-12">
          <RichText
            as={safe.headingLevel}
            html={safe.heading}
            className="text-3xl @md:text-[2.5rem] font-bold mb-4"
            data-prop="heading"
          />
          {safe.subheading && (
            <RichText
              as="div"
              html={safe.subheading}
              className="text-lg opacity-80 max-w-2xl mx-auto"
              data-prop="subheading"
            />
          )}
        </div>
        {safe.layout === "rate-table" ? (
          <RateTableLayout
            bookingTypes={bookingTypes}
            cadence={safe.cadence}
            billingDisplay={safe.billingDisplay}
            planBlurbs={safe.planBlurbs}
          />
        ) : (
          <PanelsLayout
            bookingTypes={bookingTypes}
            ctaLabel={safe.ctaLabel}
            ctaHref={safe.ctaHref}
            ctaColor={safe.ctaColor}
            showCta={safe.showCta}
            cadence={safe.cadence}
            billingDisplay={safe.billingDisplay}
            highlightIndex={safe.highlightIndex}
            planBlurbs={safe.planBlurbs}
            SubH={SubH}
          />
        )}
        {safe.footnote && (
          <RichText
            as="p"
            html={safe.footnote}
            className="mt-6 text-sm text-center opacity-80 max-w-3xl mx-auto"
            data-prop="footnote"
          />
        )}
    </SectionShell>
  );
}
